import Entity from 'models/entity';
import { AuditType, AuditUserRole } from 'models/internalAudit';
import { InternalAuditScope } from 'models/internalAuditScope';
import InternalAuditTemplate from 'models/internalAuditTemplate';
import RecurringPattern from 'models/recurringPattern';
import { newGuid } from 'utils/guid';

export class InternalAuditMonitoringUpgradePreviewRequest {
  sourceTaskIds: number[];

  auditYear: number;

  scopeItems?: Entity[];

  constructor() {
    this.sourceTaskIds = [];
    this.auditYear = new Date().getFullYear();
  }
}

export class InternalAuditMonitoringUpgradeSource {
  sourceTaskId: number;

  versionHash: string;

  name: string;

  description?: string;

  ownerId?: string;

  ownerName?: string;

  assigneeId?: string;

  assigneeName?: string;

  authSchemaId?: number;

  taskTypeId?: number;

  checklistItemCount: number;

  // parsed in the mapping: the API sends serialized recurrence JSON, which must never reach the UI as
  // text. An inactive pattern is a source without a recurrence.
  recurrencePattern: RecurringPattern;

  instanceCount: number;

  completedInstanceCount: number;

  latestCompletedOn?: Date;

  constructor() {
    this.sourceTaskId = 0;
    this.versionHash = '';
    this.name = '';
    this.checklistItemCount = 0;
    this.recurrencePattern = new RecurringPattern();
    this.instanceCount = 0;
    this.completedInstanceCount = 0;
  }
}

export class InternalAuditMonitoringUpgradeRolePermission {
  role: AuditUserRole;

  granted: boolean;

  reason: number;

  authSchemaName?: string;

  constructor() {
    this.role = AuditUserRole.Auditor;
    this.granted = false;
    this.reason = 0;
  }
}

export class InternalAuditMonitoringUpgradeTeamCandidate {
  userId: string;

  name?: string;

  candidateRoles: AuditUserRole[];

  suggestedRole?: AuditUserRole;

  requiresDecision: boolean;

  sourceTaskIds: number[];

  rolePermissions: InternalAuditMonitoringUpgradeRolePermission[];

  constructor() {
    this.userId = '';
    this.candidateRoles = [];
    this.requiresDecision = false;
    this.sourceTaskIds = [];
    this.rolePermissions = [];
  }
}

export class InternalAuditMonitoringUpgradeScopeSource {
  entityType: number;

  entityId: number;

  sourceTaskIds: number[];

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.sourceTaskIds = [];
  }
}

export class InternalAuditMonitoringUpgradeProcedureSource {
  sourceTaskId: number;

  sourceName: string;

  checklistItemCount: number;

  assigneeId?: string;

  assigneeName?: string;

  constructor() {
    this.sourceTaskId = 0;
    this.sourceName = '';
    this.checklistItemCount = 0;
  }
}

export class InternalAuditMonitoringUpgradeProcedureScopeItem {
  entityType: number;

  entityId: number;

  code?: string;

  name?: string;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
  }
}

/**
 * One audit procedure the server will create from the selected monitoring sources. Read-only: the
 * procedures are computed, not chosen, so this is only ever shown.
 */
export class InternalAuditMonitoringUpgradeProcedure {
  key: string;

  name?: string;

  taskTypeId?: number;

  formName?: string;

  checklistItemCount: number;

  sources: InternalAuditMonitoringUpgradeProcedureSource[];

  // empty means the procedure is created but no scope item points at it: everything it covers already
  // has an earlier procedure of the same overlap
  scopeItems: InternalAuditMonitoringUpgradeProcedureScopeItem[];

  constructor() {
    this.key = '';
    this.checklistItemCount = 0;
    this.sources = [];
    this.scopeItems = [];
  }
}

export default class InternalAuditMonitoringUpgradePreview {
  auditYear: number;

  suggestedName: string;

  suggestedDescription?: string;

  suggestedAuditType: AuditType;

  suggestedAuthSchemaId?: number;

  authSchemaRequiresSelection: boolean;

  sources: InternalAuditMonitoringUpgradeSource[];

  teamCandidates: InternalAuditMonitoringUpgradeTeamCandidate[];

  teamRequiresResolution: boolean;

  proposedScope: InternalAuditScope;

  scopeSources: InternalAuditMonitoringUpgradeScopeSource[];

  procedures: InternalAuditMonitoringUpgradeProcedure[];

  sourcesWithoutScope: number[];

  suggestedRecurrencePattern?: string;

  recurrenceCompatible: boolean;

  warnings: string[];

  existingInternalAuditTemplateId?: number;

  existingInternalAuditId?: number;

  constructor() {
    this.auditYear = new Date().getFullYear();
    this.suggestedName = '';
    this.suggestedAuditType = AuditType.Internal;
    this.authSchemaRequiresSelection = false;
    this.sources = [];
    this.teamCandidates = [];
    this.teamRequiresResolution = false;
    this.proposedScope = new InternalAuditScope();
    this.scopeSources = [];
    this.procedures = [];
    this.sourcesWithoutScope = [];
    this.recurrenceCompatible = false;
    this.warnings = [];
  }
}

export class InternalAuditMonitoringUpgradeSourceVersion {
  sourceTaskId: number;

  versionHash: string;

  constructor() {
    this.sourceTaskId = 0;
    this.versionHash = '';
  }
}

export class InternalAuditMonitoringUpgradeRequest {
  clientRequestId: string;

  template: InternalAuditTemplate;

  sources: InternalAuditMonitoringUpgradeSourceVersion[];

  scopeItems: Entity[];

  auditYear: number;

  constructor() {
    this.clientRequestId = newGuid();
    this.template = new InternalAuditTemplate();
    this.sources = [];
    this.scopeItems = [];
    this.auditYear = new Date().getFullYear();
  }
}

export class InternalAuditMonitoringUpgradeResult {
  internalAuditTemplateId: number;

  internalAuditId: number;

  existing: boolean;

  warnings: string[];

  constructor() {
    this.internalAuditTemplateId = 0;
    this.internalAuditId = 0;
    this.existing = false;
    this.warnings = [];
  }
}

export enum InternalAuditMonitoringSourceDeactivationResult {
  LeftActive = 0,
  Completed = 1,
}

export class InternalAuditMonitoringSource {
  sourceTaskId: number;

  name: string;

  upgradedOn?: Date;

  auditYear: number;

  deactivationResult: InternalAuditMonitoringSourceDeactivationResult;

  completedInstanceCount: number;

  latestCompletedOn?: Date;

  constructor() {
    this.sourceTaskId = 0;
    this.name = '';
    this.auditYear = 0;
    this.deactivationResult = InternalAuditMonitoringSourceDeactivationResult.LeftActive;
    this.completedInstanceCount = 0;
  }
}
